import React, { useMemo } from 'react';

import { Button, DataTable, Lozenge, StyleProvider } from '@attentive-platform/stem-ui';

import TrashIcon from '../../Assets/TrashIcon';
import TransferIcon from '../../Assets/TransferIcon';
import { BID_ROLE_ENUM, ROLE_STYLE_MAP, STATUS_LOZENGE_MAP } from './constants';

const RoleBadge = ({ role }) => {
  const config = ROLE_STYLE_MAP[role];
  return (
    <span
      className='inline-block px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap'
      style={{ backgroundColor: config.bg, color: config.text }}>
      {config.label}
    </span>
  );
};

const ADD_NEW_ROW = { _isAddRow: true };

const MembersTable = ({ members, disabled, onTransfer, onRemove, onAdd }) => {
  const tableData = useMemo(() => [ADD_NEW_ROW, ...members], [members]);

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: ({ row }) => {
          if (row.original._isAddRow) {
            return (
              <Button size='small' variant='text' color='primary' disabled={disabled} onClick={onAdd}>
                + Add new
              </Button>
            );
          }
          const member = row.original;
          return (
            <div className='flex items-center gap-3'>
              <div className='w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600 shrink-0'>
                {member.email?.charAt(0)?.toUpperCase() || '-'}
              </div>
              <div className='min-w-0'>
                <div className='text-sm font-medium text-gray-900 truncate'>{member.name || '-'}</div>
                <div className='text-xs text-gray-500 truncate'>{member.email || '-'}</div>
              </div>
            </div>
          );
        }
      },
      {
        Header: 'Role',
        accessor: 'role',
        Cell: ({ row, value }) => {
          if (row.original._isAddRow) return null;
          return <RoleBadge role={value} />;
        }
      },
      {
        Header: () => <span className='whitespace-nowrap'>Member since</span>,
        accessor: 'member_since',
        Cell: ({ row, value }) => {
          if (row.original._isAddRow) return null;
          return (
            <span className='whitespace-nowrap'>
              {value
                ? new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })
                : '-'}
            </span>
          );
        }
      },
      {
        Header: 'Status',
        accessor: 'id',
        Cell: ({ row, value }) => {
          if (row.original._isAddRow) return null;
          const key = value ? 'ACTIVE_MEMBER' : 'PENDING';
          const config = STATUS_LOZENGE_MAP[key];
          return <Lozenge color={config.color} variant={config.variant} label={config.label} size='small' />;
        }
      },
      {
        Header: 'Actions',
        accessor: '_actions',
        Cell: ({ row }) => {
          if (row.original._isAddRow) return null;
          const memberIndex = row.index - 1;
          const isPrimaryAdmin = row.original.role === BID_ROLE_ENUM.PRIMARY_ADMIN;
          return (
            <div className='flex items-center gap-2'>
              <Button size='small' variant='text' disabled={disabled} onClick={() => onTransfer(row.original)}>
                <TransferIcon />
              </Button>
              {!isPrimaryAdmin && (
                <Button
                  size='small'
                  variant='text'
                  color='error'
                  disabled={disabled}
                  onClick={() => onRemove(memberIndex)}>
                  <TrashIcon />
                </Button>
              )}
            </div>
          );
        }
      }
    ],
    [disabled, onAdd, onTransfer, onRemove]
  );

  return (
    <StyleProvider>
      <DataTable
        data={tableData}
        columns={columns}
        allowBulkSelect={false}
        options={{ showFooter: false }}
        rowProps={{ isSelectable: () => false }}
        paginationProps={{ hidePagination: true, pageSize: tableData.length }}
      />
    </StyleProvider>
  );
};

export default MembersTable;
